import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchOrganizations,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  mergeOrganizations,
  fetchOrganizationById,
  fetchOrganizationHierarchy,
  fetchOrganizationChildren,
  fetchRootOrganizations,
} from '@/services';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { CACHE_TIMES, invalidateRelatedQueries } from '@/lib/react-query';

export function useOrganizations() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { session, user } = useAuth();

  const organizationsQuery = useQuery({
    queryKey: ['organizations'],
    queryFn: fetchOrganizations,
    ...CACHE_TIMES.REFERENCE, // Organizations change occasionally
    enabled: !!session, // Only run query when user is authenticated
  });

  const createOrganizationMutation = useMutation({
    mutationFn: createOrganization,
    onSuccess: () => {
      invalidateRelatedQueries(['organizations', 'organization-hierarchy', 'root-organizations']);
      toast({
        title: 'Organization created',
        description: 'The organization has been successfully created.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to create organization',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateOrganizationMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => updateOrganization(id, data),
    onSuccess: () => {
      invalidateRelatedQueries(['organizations', 'organization-hierarchy', 'root-organizations']);
      toast({
        title: 'Organization updated',
        description: 'The organization has been successfully updated.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to update organization',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const deleteOrganizationMutation = useMutation({
    mutationFn: deleteOrganization,
    onSuccess: () => {
      invalidateRelatedQueries(['organizations', 'organization-hierarchy', 'root-organizations']);
      toast({
        title: 'Organization deleted',
        description: 'The organization has been successfully deleted.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to delete organization',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const mergeOrganizationsMutation = useMutation({
    mutationFn: ({ sourceId, targetId }: { sourceId: string; targetId: string }) =>
      mergeOrganizations(sourceId, targetId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast({
        title: 'Organizations merged',
        description: data.message || 'The organizations have been successfully merged.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to merge organizations',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const getOrganizationQuery = (id: string) =>
    useQuery({
      queryKey: ['organizations', id],
      queryFn: () => fetchOrganizationById(id),
      ...CACHE_TIMES.REFERENCE,
      enabled: !!session && !!id,
    });

  const getOrganizationHierarchyQuery = (id: string) =>
    useQuery({
      queryKey: ['organization-hierarchy', id],
      queryFn: () => fetchOrganizationHierarchy(id),
      ...CACHE_TIMES.REFERENCE,
      enabled: !!session && !!id,
    });

  const getOrganizationChildrenQuery = (parentId: string) =>
    useQuery({
      queryKey: ['organization-children', parentId],
      queryFn: () => fetchOrganizationChildren(parentId),
      ...CACHE_TIMES.REFERENCE,
      enabled: !!session && !!parentId,
    });

  const getRootOrganizationsQuery = () =>
    useQuery({
      queryKey: ['organizations-root'],
      queryFn: fetchRootOrganizations,
      ...CACHE_TIMES.REFERENCE,
      enabled: !!session,
    });

  return {
    organizations: organizationsQuery.data || [],
    isLoading: organizationsQuery.isLoading,
    error: organizationsQuery.error,
    getOrganizationById: getOrganizationQuery,
    getOrganizationHierarchy: getOrganizationHierarchyQuery,
    getOrganizationChildren: getOrganizationChildrenQuery,
    getRootOrganizations: getRootOrganizationsQuery,
    createOrganization: createOrganizationMutation.mutate,
    updateOrganization: updateOrganizationMutation.mutate,
    deleteOrganization: deleteOrganizationMutation.mutate,
    mergeOrganizations: mergeOrganizationsMutation.mutate,
  };
}

export function useOrganizationHierarchy(organizationId?: string) {
  const { session } = useAuth();
  
  return useQuery({
    queryKey: ['organization-hierarchy', organizationId || 'all'],
    queryFn: async () => {
      if (organizationId) {
        const hierarchy = await fetchOrganizationHierarchy(organizationId);
        return hierarchy ? [hierarchy] : [];
      } else {
        // Fetch all root organizations and their hierarchies
        const roots = await fetchRootOrganizations();
        const hierarchies = await Promise.all(
          roots.map((root) => fetchOrganizationHierarchy(root.id))
        );
        return hierarchies.filter(Boolean);
      }
    },
    enabled: !!session,
  });
}
